/**
 * Shared navigation constants for BottomTabBar and DesktopSidebar
 *
 * Single source of truth for navigation items and their SVG icons.
 * Icons are app-local SVGs so shell tests do not depend on an icon package.
 */

import type { CSSProperties, FC } from 'react';

export interface NavItem {
  id: string;
  label: string;
  path: string;
}

/**
 * The 5 primary navigation destinations for Aurora.
 */
export const NAV_ITEMS: NavItem[] = [
  { id: 'today', label: 'Today', path: '/' },
  { id: 'connections', label: 'Connections', path: '/connections' },
  { id: 'inbox', label: 'Inbox', path: '/inbox' },
  { id: 'wallet', label: 'Wallet', path: '/wallet' },
  { id: 'you', label: 'You', path: '/you' },
];

interface IconProps {
  className?: string;
  style?: CSSProperties;
}

/** Today tab icon */
function TodayIcon({ className, style }: IconProps) {
  return (
    <svg
      className={className}
      style={style}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M12 2C12 2 4.5 9.5 4.5 14.5C4.5 17.5 6.5 20 9 21L9.5 18.5C9.5 18.5 10.5 17 12 17C13.5 17 14.5 18.5 14.5 18.5L15 21C17.5 20 19.5 17.5 19.5 14.5C19.5 9.5 12 2 12 2ZM12 14C10.9 14 10 13.1 10 12C10 10.9 10.9 10 12 10C13.1 10 14 10.9 14 12C14 13.1 13.1 14 12 14Z" />
    </svg>
  );
}

/** Piggy bank icon for Wallet tab */
function PiggyBankIcon({ className, style }: IconProps) {
  return (
    <svg
      className={className}
      style={style}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M19.83 7.5L17.56 5.23C17.21 4.88 16.74 4.68 16.25 4.68H14V3C14 2.45 13.55 2 13 2H10C9.45 2 9 2.45 9 3V4.68H7.75C6.51 4.68 5.5 5.69 5.5 6.93V7.5H4C3.45 7.5 3 7.95 3 8.5V10.5C3 11.05 3.45 11.5 4 11.5H5.5V14.07C5.5 15.31 6.51 16.32 7.75 16.32H8V18C8 18.55 8.45 19 9 19H10C10.55 19 11 18.55 11 18V16.32H13V18C13 18.55 13.45 19 14 19H15C15.55 19 16 18.55 16 18V16.32H16.25C17.49 16.32 18.5 15.31 18.5 14.07V11.5H19.5C19.78 11.5 20 11.28 20 11V8C20 7.72 19.83 7.5 19.83 7.5ZM16 10C15.45 10 15 9.55 15 9C15 8.45 15.45 8 16 8C16.55 8 17 8.45 17 9C17 9.55 16.55 10 16 10Z" />
    </svg>
  );
}

/** Connections tab icon */
function ConnectionsIcon({ className, style }: IconProps) {
  return (
    <svg
      className={className}
      style={style}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm-5-9h3.17l1.41-1.41L12 8.17 10.59 9.59 9.17 11H7v2h2.17l1.42 1.41L12 15.83l1.41-1.42L14.83 13H17v-2h-2.17l-1.42-1.41L12 8.17l-1.41 1.42L9.17 11H7v2zm5 3a2 2 0 100-4 2 2 0 000 4z" />
    </svg>
  );
}

/** Inbox icon */
function InboxIcon({ className, style }: IconProps) {
  return (
    <svg
      className={className}
      style={style}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM20 16H5.17L4 17.17V4H20V16ZM7 9H9V11H7V9ZM11 9H13V11H11V9ZM15 9H17V11H15V9Z" />
    </svg>
  );
}

/** You tab icon */
function YouIcon({ className, style }: IconProps) {
  return (
    <svg
      className={className}
      style={style}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M19.14 12.94C19.18 12.64 19.2 12.33 19.2 12C19.2 11.68 19.18 11.36 19.13 11.06L21.16 9.48C21.34 9.34 21.39 9.07 21.28 8.87L19.36 5.55C19.24 5.33 18.99 5.26 18.77 5.33L16.38 6.29C15.88 5.91 15.35 5.59 14.76 5.35L14.4 2.81C14.36 2.57 14.16 2.4 13.92 2.4H10.08C9.84 2.4 9.65 2.57 9.61 2.81L9.25 5.35C8.66 5.59 8.12 5.92 7.63 6.29L5.24 5.33C5.02 5.25 4.77 5.33 4.65 5.55L2.74 8.87C2.62 9.08 2.66 9.34 2.86 9.48L4.89 11.06C4.84 11.36 4.8 11.69 4.8 12C4.8 12.31 4.82 12.64 4.87 12.94L2.84 14.52C2.66 14.66 2.61 14.93 2.72 15.13L4.64 18.45C4.76 18.67 5.01 18.74 5.23 18.67L7.62 17.71C8.12 18.09 8.65 18.41 9.24 18.65L9.6 21.19C9.65 21.43 9.84 21.6 10.08 21.6H13.92C14.16 21.6 14.36 21.43 14.39 21.19L14.75 18.65C15.34 18.41 15.88 18.09 16.37 17.71L18.76 18.67C18.98 18.75 19.23 18.67 19.35 18.45L21.27 15.13C21.39 14.91 21.34 14.66 21.15 14.52L19.14 12.94ZM12 15.6C10.02 15.6 8.4 13.98 8.4 12C8.4 10.02 10.02 8.4 12 8.4C13.98 8.4 15.6 10.02 15.6 12C15.6 13.98 13.98 15.6 12 15.6Z" />
    </svg>
  );
}

/**
 * Map of nav item ID to its icon component.
 * Both BottomTabBar and DesktopSidebar use this.
 */
export const NAV_ICONS: Record<string, FC<IconProps>> = {
  today: TodayIcon,
  connections: ConnectionsIcon,
  inbox: InboxIcon,
  wallet: PiggyBankIcon,
  you: YouIcon,
};

/**
 * Page title mapping from pathname to display title.
 * Used by TopBar to show context-appropriate title.
 */
export const PAGE_TITLES: Record<string, string> = {
  '/': 'Today',
  '/wallet': 'Wallet',
  '/notifications': 'Notifications',
  '/downloads': 'Downloads',
  '/settings': 'Settings',
  '/you': 'You',
  '/you/timeline': 'You',
  '/profile': 'You',
  '/ikigai': 'You',
  '/inbox': 'Inbox',
  '/onboarding': 'Welcome',
  '/connections': 'Connections',
  '/basket': 'Basket',
  '/dev/aurora': 'Aurora',
};
